/**
@format
@flow local-strict
*/
import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import BannerNotification from '@ziynx/components/notifications';
import { Button } from '@ziynx/components/buttons';
import Colors from '@ziynx/theme/colors';
import Input from '@ziynx/components/inputs';
import { withAppManager } from '@ziynx/components/utilities';
import Link from '@ziynx/components/links';
import { Body } from '@ziynx/components/typography';
import { ContentContainer } from '@ziynx/components/layout';
import { Form, FormRow } from '@ziynx/components/forms';
import Image from '@ziynx/components/images';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import logo from '../../assets/ZIYNX_LOGO_2.png';
import logoSmall from '../../assets/ZIYNX_SYMBOL_2 2x.png';

type Props = $ReadOnly<{|

  /**
   * Navigation stack
   */
  history: Object,

  /**
   * Location represents where
   * the app is at right now
   */
  location: Object,
|}>;

// Replace with card ??
const FormSectionWrapper = styled.div`
  display: table;
  margin: 0 auto;
  ${({ viewport }) => (viewport === 'mobileSmall' || viewport === 'mobile') && `
    padding-left: 16px;
    padding-right: 16px;
    padding-bottom: 34px;
`};
`;

const StyledBannerWrapper = styled.div`
  z-index: 996;
  width: 100%;
  height: 0;
`;

const StyledBodyWrapper = styled.div`
  padding-bottom: 40px;
  ${({ center }) => center && 'text-align: center;'}
`;

const FormSubTitle = styled(StyledBodyWrapper)`
  max-width: 480px;
  padding-bottom: 48px;
`;

const StyledButton = styled(Button)`
  margin-bottom: 16px;
`;

const ImageWrapper = styled.div`
  padding-bottom: 64px;
  padding-top: ${({ viewport }) => ((viewport === 'mobile' || viewport === 'mobileSmall' || viewport === 'tablet') ? 32 : 48)}px;
`;

/**
 * Object to hold data for each types of inputs to
 * be used in Controller
 */
const inputsData = {
  data: [
    {
      id: '1',
      name: 'confirmCode',
      defaultValue: '',
      regExPattern: /^[0-9]{6}$/,
      errorMessage: 'Please enter 6-digit code',
    },
    {
      id: '2',
      name: 'newPassword',
      defaultValue: '',
      regExPattern: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z!@#$%^ˆ&*?_\-+=()\\|<>/{}[\]:;.,~˜`'"]{8,}$/im,
      errorMessage: 'Password is too weak. Use at least 8 characters and 1 number',
    },
  ],
};

/**
 * Time for banner to setTimeout
 */
const bannerTimeOut = 5000;

/**
 * Object to store common error messages for banner notification
 * NOTE: will either delete some messages once figured out built in amplify error.message
 */
const bannerMessageData = {
  maxAttemptResendingError:
    'You have reached the maximum attempt to request a verification code. Please try again later.',
  maxAttemptResettingError:
    'You have reached the maximum attempt to reset your password. Please try again later.',
  generalResettingError:
    'We ran into some trouble resetting your password. Please try again later.',
  generalResendingError:
    'An error occurred while re-sending your verification code. Please try again later.',
  successResendingCode:
    'An email with your verification code has been sent again. Please check your mailbox and spam folder.',
};
/**
 * ResetPassword screen where user enters in verification code
 * sent via email and resets their password
 */
const ResetPasswordScreen = (props: ManagedProps<Props>): React$Node => {
  const { history, viewport } = props;
  const {
    handleSubmit, control, errors, setError, clearErrors, formState,
  } = useForm();

  const { location: { state } = {} } = props;
  const username = state?.email;

  // Fields to watch for changes or errors
  const { dirtyFields } = formState;

  // When code is re-sent
  const [codeResent, setCodeResent] = useState(false);
  // When code re-sent or resetting password has error
  const [codeError, setCodeError] = useState(false);
  // state for banner error message
  const [bannerErrorMessage, setBannerErrorMessage] = useState('');
  // Shows if waiting for response from AWS
  const [isLoading, setIsLoading] = useState(false);
  // State to keep track if banner should be rendered
  const [shouldRenderBanner, setRenderBanner] = useState(false);

  const formSubtitle = `We sent a verification code to ${!username ? 'your email' : username}. Please check your email and follow the instructions.`;

  /**
   * Fired when amplify verifies new password and verification code
   * currently routes to login screen
   */
  const goBackToLogin = (): void => {
    history.push('/');
  };

  /**
   * Function to signal there's an error
   * and render banner
   */
  const setBannerError = (): void => {
    setCodeError(true);
    setRenderBanner(true);
  };

  /**
   * Function to re-send code to reset password
   * Will likely to make this a util function since
   * ConfirmCode page also uses this
   */
  const resendCode = async (): Promise<any> => {
    setIsLoading(true);
    try {
      await Auth.forgotPassword(username);

      setIsLoading(false);
      if (codeError) setCodeError(false); // In case of successful re-try when errored out
      setCodeResent(true);
      setRenderBanner(true);
    } catch (error) {
      setRenderBanner(false); // Reset banner state
      setCodeError(false);
      setIsLoading(false);
      switch (error.code) {
      case 'LimitExceededException':
      case 'TooManyRequestsException':
      case 'CodeDeliveryFailureException':
      case 'InternalErrorException':
      default:
        setBannerErrorMessage(error.message);
        break;
      }
      if (codeResent) setCodeResent(false);
      setBannerError();
    }
  };

  const sendCode = async (): Promise<any> => {
    try {
      await Auth.forgotPassword(username);
    } catch (error) {
      setRenderBanner(false); // Reset banner state
      setBannerErrorMessage(error.message);
      setBannerError();
    } finally {
      // We only want to send the code once
      history.replace('/reset-password', { email: username, sendCode: false });
    }
  };

  useEffect(() => {
    if (username && state?.sendCode) {
      sendCode();
    }
  }, [state?.sendCode, username]);

  /**
   * Fired when user hits submit button.
   * Amplify will validate verification code and password
   * and react-hook-form will throw error to these fields, if any
   * @see https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_ConfirmForgotPassword.html for error codes
   */
  const onSubmit = async (data: Object): Promise<void> => {
    setIsLoading(true);
    const { confirmCode: code, newPassword } = data || {};
    try {
      await Auth.forgotPasswordSubmit(username, code, newPassword);

      goBackToLogin();
    } catch (error) {
      setRenderBanner(false); // Reset banner state
      setIsLoading(false);
      setCodeError(false);
      switch (error.code) {
      case 'ExpiredCodeException':
      // Thrown when code is expired, prompting user
      // to re-start process
        history.push('/confirm-email');
        break;

      case 'CodeMismatchException':
        setError('confirmCode', {
          type: 'manual',
          message: error.message,
        });
        break;

      case 'InvalidPasswordException':
      case 'InvalidParameterException':
        setError('newPassword', {
          type: 'manual',
          message: error.message,
        });
        break;

      case 'LimitExceededException':
      case 'TooManyRequestsException':
      default:
        // Will handle for any internal errors and display that error's message
        // i.e InternalErrorException, TooManyFailedAttemptsException,...
        setBannerError();
        setBannerErrorMessage(error.message);
        break;
      }
    }
  };

  /**
   * Function to render Inputs for Controller render prop
   * of useForm hook
   */
  const renderInputs = ({ onChange, onFocus, name }): React$Node => {
    let label = '';
    let variant = 'text'; // default type of inputs
    switch (name) {
    case 'confirmCode':
      label = 'Verification code';
      break;
    case 'newPassword':
    default:
      label = 'New password';
      variant = 'password';
      break;
    }

    /**
     * Sets the value of input
     */
    const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
      (event?.target: HTMLInputElement);
      onChange(event?.target?.value);
    };

    /**
     * Function to clear any errors of inputs
     * when on focus event, if any
     */
    const onInputFocus = (): any => {
      if (errors?.[name]) clearErrors(name);
      return onFocus;
    };

    return (
      <Input
        autoComplete="new-password"
        variant={variant}
        error={!!errors?.[name]}
        errorLabel={errors?.[name]?.message}
        inputName={name}
        label={label}
        onChange={onInputChange}
        onFocus={onInputFocus}
        required
      />
    );
  };

  /**
   * Function to map out FormRows
   * that holds the Controller of each types of inputs
   * based on inputsData
   */
  const renderInputsControllers = (): React$Node => inputsData.data.map((field) => (
    <FormRow key={field.id}>
      <Controller
        control={control}
        render={renderInputs}
        name={field.name}
        defaultValue={field.defaultValue}
        rules={{
          required: 'Please enter a value to continue',
          pattern: {
            value: field?.regExPattern,
            message: field?.errorMessage,
          },
        }}
      />
    </FormRow>
  ));

  /**
   * Function to check if dirtyFields length equals to inputsData length.
   * Returns true if not -> disable button
   */
  const isDisabled = (): boolean => {
    if (!inputsData?.data || !dirtyFields) return true;
    return Object.keys(dirtyFields).length !== inputsData?.data?.length;
  };

  /**
   * Function to watch if an input has error
   * after user submit upon validation.
   * Errors object is {} by default - which means no error
   * Returns true if length of errors object != 0
   * and false if empty
   */
  const isError = (): boolean => !!Object.keys(errors).length;

  /**
   * To render ziynx logo as image
   */
  const renderLogo = (): React$Node => {
    if (viewport !== 'desktop' && viewport !== 'desktopSmall') {
      return (
        <Image src={logoSmall} width="24px" height="24px" alt="company-logo" />
      );
    }
    return (
      <Image src={logo} width="114px" height="24px" alt="company-logo" />
    );
  };

  return (
    <>
      <Helmet>
        <title>Reset Password | ZIYNX</title>
      </Helmet>
      <ContentContainer offsetTop={0} isVertical>
        {shouldRenderBanner && (
          <StyledBannerWrapper>
            <BannerNotification animated timeout={bannerTimeOut} variant={codeError ? 'error' : 'success'}>
              {!codeError && codeResent && bannerMessageData.successResendingCode}
              {codeError && bannerErrorMessage}
            </BannerNotification>
          </StyledBannerWrapper>
        )}
        <FormSectionWrapper viewport={viewport} hasBanner={shouldRenderBanner}>
          <ImageWrapper viewport={viewport}>
            {renderLogo()}
          </ImageWrapper>
          <Form
            formID="reset-password"
            title="Reset your password"
          >
            <FormSubTitle>
              <Body medium color={Colors.GrayDark}>
                {formSubtitle}
              </Body>
            </FormSubTitle>

            {renderInputsControllers()}

            <StyledBodyWrapper>
              <Body medium color={Colors.GrayVeryDark}>
                Didn&apos;t get your code?&nbsp;
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link variant="inline" onClick={resendCode}>Resend code</Link>
              </Body>
            </StyledBodyWrapper>

            <StyledButton
              disabled={isDisabled() || isError()}
              isLoading={isLoading}
              width="100%"
              type="submit"
              form="reset-password"
              onClick={handleSubmit(onSubmit)}
            >
              Submit
            </StyledButton>
            <StyledBodyWrapper center>
              <Link
                variant="navigation"
                activeOnlyWhenExact
                label="Back to Log in"
                to="/"
              />
            </StyledBodyWrapper>
          </Form>
        </FormSectionWrapper>
      </ContentContainer>
    </>
  );
};

ResetPasswordScreen.defaultProps = {
  viewport: 'desktop',
};

export default (withAppManager(ResetPasswordScreen): React$ComponentType<
  React$Config<ManagedProps<Props>, typeof ResetPasswordScreen.defaultProps>
>);
